import { Card, CardActions, CardContent, Grid, Skeleton } from '@mui/material';
import {
	CerebroDrawer,
	CerebroIcon,
	CerebroLink,
	CerebroSecondaryButton,
	CerebroText,
} from '../../../shared/components';
import { useTermSheetRevisionsQuery } from '../../../shared/hooks';
import { TermSheet, TermSheetRevision } from '../../../shared/models';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar } from '@fortawesome/free-solid-svg-icons';
import { DateUtils, EnumUtils, FileUtils } from '../../../shared/utils';
import { CerebroApiEndpoints } from '../../../shared/utils/api';
import React from 'react';
import { useAuth } from '../../../shared/context';
import { DeleteRevisionDialog } from '../Employees/DeleteRevisionDialog';

interface RevisionListDrawerProps {
	dataRoomGuid: string | undefined;
	sheet: TermSheet | null;
	editRevision: (revision: TermSheetRevision | null) => void;
	close: () => void;
}

export const RevisionListDrawer: React.FC<RevisionListDrawerProps> = ({ dataRoomGuid, sheet, editRevision, close }) => {
	const { currentUser } = useAuth();
	const revisionsQuery = useTermSheetRevisionsQuery(dataRoomGuid, sheet?.guid, {
		enabled: sheet != null,
	});
	const [deleteRevision, setDeleteRevision] = React.useState<TermSheetRevision | null>(null);

	return (
		<CerebroDrawer open={sheet != null} onClose={close}>
			{revisionsQuery.isLoading && (
				<>
					<Skeleton height={100} width={400} />
					<Skeleton height={100} width={400} />
				</>
			)}

			{revisionsQuery.isSuccess && (
				<>
					<Grid container spacing={2} alignItems="stretch">
						{revisionsQuery.data.map(revision => {
							const canDelete = currentUser.isEmployeeUser;

							return (
								<Grid item key={revision.guid} xs={12} sx={{ marginTop: 5 }}>
									<Card
										key={revision.guid}
										elevation={3}
										sx={{
											display: 'flex',
											justifyContent: 'space-between',
											flexDirection: 'column',
											height: '100%',
											marginBottom: 2,
										}}
									>
										<CardContent sx={{ paddingBottom: '0 !important' }}>
											<Grid container sx={{ marginBottom: 2, width: 500 }}>
												<Grid item xs={8} sx={{ display: 'flex' }}>
													{revision.isCurrentVersion && (
														<CerebroIcon
															padRight
															sx={theme => ({ color: theme.palette.success.main })}
															title="Current Version"
														>
															<FontAwesomeIcon icon={faStar} size="sm" />
														</CerebroIcon>
													)}
													<CerebroText
														variant="large"
														component="span"
														sx={{ fontWeight: 'bold' }}
													>
														Version {revision.versionNumber}
													</CerebroText>
												</Grid>
												<Grid item xs={4} sx={{ textAlign: 'right' }}>
													{EnumUtils.GetTermSheetResponse(revision.responseStatus)}
												</Grid>
											</Grid>
											<Grid container sx={{ marginTop: 2 }}>
												<Grid item xs={1}></Grid>
												<Grid item xs={10}>
													<CerebroText gutterBottom sx={{ marginTop: 1 }}>
														Submitted by {revision.submittedByName} on{' '}
														{DateUtils.FormatStringAsShortDate(
															revision.dateReceived?.toString()
														)}
													</CerebroText>
													{revision.termSheetFileName && (
														<CerebroLink
															href={CerebroApiEndpoints.TermSheets.AbsoluteDownload(
																dataRoomGuid,
																sheet?.guid,
																revision.guid
															)}
															target="_blank"
															rel="noreferrer"
															variant="link"
														>
															{FileUtils.getFileTypeIcon(revision.termSheetFileName)}{' '}
															Download
														</CerebroLink>
													)}
												</Grid>
												<Grid item xs={1}></Grid>
											</Grid>
										</CardContent>
										<CardActions
											sx={theme => ({
												alignItems: 'flex-end',
												backgroundColor: theme.palette.gray.background,
												borderTop: `1px solid ${theme.palette.divider}`,
												justifyContent: 'right',
											})}
										>
											{canDelete && (
												<CerebroSecondaryButton onClick={() => setDeleteRevision(revision)}>
													Delete
												</CerebroSecondaryButton>
											)}
											{currentUser.isEmployeeUser && (
												<CerebroSecondaryButton onClick={() => editRevision(revision)}>
													Edit
												</CerebroSecondaryButton>
											)}
										</CardActions>
									</Card>
								</Grid>
							);
						})}
						{revisionsQuery.data.length == 0 && (
							<CerebroText>No revisions have been added to the term sheet.</CerebroText>
						)}
					</Grid>
				</>
			)}

			<DeleteRevisionDialog
				dataRoomGuid={dataRoomGuid}
				sheet={sheet}
				revision={deleteRevision}
				close={() => {
					setDeleteRevision(null);
					close();
				}}
			/>
		</CerebroDrawer>
	);
};
